
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useAuthGuard } from "@/hooks/useAuthGuard";
import { useAuth } from "@/auth/useAuth";
import { GoogleSignInButton } from "./GoogleSignInButton";
import { TermsOfServiceDialog } from "./TermsOfServiceDialog";
import { PrivacyPolicyDialog } from "./PrivacyPolicyDialog";

const RefactoredSignupForm = () => {
  const [isLogin, setIsLogin] = useState(false);
  const { error, loginError } = useAuth();
  
  // Use auth guard to redirect authenticated users
  const { isLoading } = useAuthGuard({ 
    requireAuth: false, // This page is for unauthenticated users
  });

  // Show loading state while auth guard is working
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-white to-purple-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-white to-purple-50 p-4">
      {/* Trust indicator */}
      <div className="absolute top-1/2 -translate-y-64">
        <div className="bg-purple-100 text-purple-600 px-4 py-2 rounded-full text-sm font-medium">
          Trusted by 5,000+ creators
        </div>
      </div>

      <Card className="w-full max-w-md mx-auto shadow-lg border-0 bg-white/80 backdrop-blur-sm">
        <CardContent className="p-8">
          {!isLogin ? (
            <>
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-3">
                  Start your free 7-day trial
                </h1>
                <p className="text-gray-500">No credit card required</p>
              </div>

              <div className="flex justify-center mb-8">
                <GoogleSignInButton 
                  text="signup_with"
                />
              </div>

              {/* Error display */}
              {(error || loginError) && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-600 text-sm">
                    {error?.message || loginError?.message || 'Authentication failed'}
                  </p>
                </div>
              )}

              <div className="text-center">
                <p className="text-xs text-gray-500 leading-relaxed mb-6">
                  By signing up, you agree to our{" "}
                  <TermsOfServiceDialog /> and <PrivacyPolicyDialog />
                </p>
                
                <p className="text-sm text-gray-600">
                  Already have an account?{" "}
                  <button 
                    onClick={() => setIsLogin(true)}
                    className="text-purple-600 hover:text-purple-700 font-medium underline bg-transparent border-none cursor-pointer"
                    disabled={isLoading}
                  >
                    Log in
                  </button>
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-6">
                  Welcome back
                </h1>
              </div>

              <div className="flex justify-center mb-8">
                <GoogleSignInButton 
                  text="signin_with"
                />
              </div>

              {/* Error display */}
              {(error || loginError) && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-600 text-sm">
                    {error?.message || loginError?.message || 'Authentication failed'}
                  </p>
                </div>
              )}

              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Don't have an account?{" "}
                  <button 
                    onClick={() => setIsLogin(false)}
                    className="text-purple-600 hover:text-purple-700 font-medium underline bg-transparent border-none cursor-pointer"
                    disabled={isLoading}
                  >
                    Start free trial
                  </button>
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RefactoredSignupForm;
